import { useEffect, useState } from "react";
import { http } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { maskDateInput, dateBrToIso, maskCurrencyInput, maskCaseNumberInput, cnjDvValido } from "@/lib/dateTimeMask";
import SugestaoInput from "@/components/SugestaoInput";
import { CASE_AREAS, PARTY_LABELS, KNOWN_CASE_AREAS } from "@/lib/caseAreas";
import { toTitleCasePt } from "@/lib/format";
import useProcessosMapa, { invalidarProcessosMapa, soDigitos } from "@/hooks/useProcessosMapa";
import { AUTOS_META, AUTOS_ORDEM, autosLabel } from "@/components/AutosTag";

// Formulário "Cadastrar processo", usado na aba Processos e no atalho "Cadastrar processo" da
// caixa de Publicações (Lote 74) — neste caso `initial` já vem preenchido com o que a
// publicação traz (nº, vara, comarca, área, partes, cliente) e o usuário completa o resto.
export const EMPTY_PROCESSO_FORM = {
  numero: "", apelido: "", client_id: "none",
  comarca: "", vara: "",
  case_area: "outro", case_area_custom: "",
  party_author: "", party_defendant: "",
  valor_causa: "", data_ajuizamento_text: "",
  autos: "principal", autos_nome: "",
};

// Converte o que o backend devolve (dados-da-publicacao) para o estado do formulário.
export function processoFormFromData(d) {
  const area = d.case_area || "outro";
  const known = KNOWN_CASE_AREAS.includes(area);
  return {
    ...EMPTY_PROCESSO_FORM,
    numero: d.numero || "",
    client_id: d.client_id || "none",
    comarca: d.comarca || "",
    vara: d.vara || "",
    case_area: known ? area : "outro",
    case_area_custom: known ? "" : (area === "outro" ? "" : area),
    party_author: d.party_author || "",
    party_defendant: d.party_defendant || "",
  };
}

export default function ProcessoFormDialog({ open, onOpenChange, initial = null, onSaved }) {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState(EMPTY_PROCESSO_FORM);
  // Lote 118: número que já está cadastrado = "outros autos" (execução provisória, incidente…) com etiqueta colorida.
  const mapa = useProcessosMapa();
  const existentes = mapa?.get(soDigitos(form.numero))?.todos || [];
  const usados = new Set(existentes.map((x) => x.autos));
  const livres = AUTOS_ORDEM.filter((k) => k === "outro" || !usados.has(k));
  const jaCadastrado = existentes.length > 0;
  useEffect(() => {
    // número já existente: sai de "Principal" para a 1ª etiqueta livre; número novo volta para "Principal"
    setForm((f) => {
      if (jaCadastrado && (f.autos === "principal" || usados.has(f.autos))) return { ...f, autos: livres.find((k) => k !== "principal") || "outro" };
      if (!jaCadastrado && f.autos !== "principal" && !f._autosManual) return { ...f, autos: "principal", autos_nome: "" };
      return f;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jaCadastrado, existentes.length]);

  useEffect(() => {
    if (!open) return;
    setForm(initial ? { ...EMPTY_PROCESSO_FORM, ...initial } : EMPTY_PROCESSO_FORM);
    http.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, [open, initial]);

  const resolvedCaseArea = (f) =>
    f.case_area === "outro" && f.case_area_custom.trim() ? f.case_area_custom.trim() : f.case_area || null;

  const submit = async () => {
    try {
      const r = await http.post("/processos", {
        numero: form.numero.trim(),
        apelido: form.apelido || null,
        client_id: form.client_id === "none" ? null : form.client_id,
        comarca: form.comarca.trim() || null,
        vara: form.vara.trim() || null,
        case_area: resolvedCaseArea(form),
        party_author: form.party_author.trim() || null,
        party_defendant: form.party_defendant.trim() || null,
        valor_causa: form.valor_causa.trim() || null,
        data_ajuizamento: form.data_ajuizamento_text ? dateBrToIso(form.data_ajuizamento_text) || null : null,
        autos: form.autos || "principal",
        autos_nome: form.autos === "outro" ? form.autos_nome.trim() || null : null,
      });
      invalidarProcessosMapa();
      toast.success("Processo cadastrado");
      onOpenChange(false);
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao cadastrar");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
          <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-display">Cadastrar processo</DialogTitle></DialogHeader>
              <div className="space-y-4 max-h-[70vh] overflow-y-auto pr-1">
                <div>
                  <Label className="text-xs">Número CNJ (20 dígitos)</Label>
                  <SugestaoInput
                    testid="processo-numero-input"
                    fonte="numeros"
                    placeholder="_______-__.____._.__.____"
                    inputMode="numeric"
                    fantasma="_______-__.____._.__.____"
                    transformar={maskCaseNumberInput}
                    value={form.numero}
                    onValueChange={(v) => setForm((f) => ({ ...f, numero: v }))}
                  />
                  {cnjDvValido(form.numero) === false && (
                    <p className="mt-1 text-[11px] text-amber-700" data-testid="processo-numero-dv-aviso">
                      O dígito verificador deste número não confere. Vale rever a digitação (você ainda pode salvar).
                    </p>
                  )}
                </div>
                {(jaCadastrado || form.autos !== "principal") && (
                  <div data-testid="processo-autos-bloco" className="rounded-md border border-violet-200 bg-violet-50/60 p-2.5 space-y-2">
                    {jaCadastrado && (
                      <p className="text-xs text-violet-900" data-testid="processo-autos-aviso">
                        Este número já está cadastrado ({existentes.map((x) => autosLabel(x.autos, x.autos_nome)).join(", ")}). Cadastre aqui os <b>outros autos</b> e escolha a etiqueta:
                      </p>
                    )}
                    <div className="flex flex-wrap gap-1.5">
                      {livres.map((k) => {
                        const m = AUTOS_META[k]; const sel = form.autos === k;
                        return (
                          <button key={k} type="button" data-testid={`processo-autos-op-${k}`} aria-pressed={sel}
                            onClick={() => setForm({ ...form, autos: k, _autosManual: true })}
                            className={`inline-flex items-center gap-1.5 min-h-[36px] px-3 rounded-full border text-xs font-semibold ${sel ? `${m.cls} ring-2 ring-offset-1 ring-stone-400` : "bg-white border-stone-300 text-stone-700 hover:border-stone-500"}`}>
                            <span className={`w-2 h-2 rounded-full ${m.dot}`} /> {m.label}
                          </button>
                        );
                      })}
                    </div>
                    {form.autos === "outro" && (
                      <Input data-testid="processo-autos-nome" value={form.autos_nome} maxLength={40} onChange={(e) => setForm({ ...form, autos_nome: e.target.value })} placeholder="Nome (ex.: Incidente de desconsideração)" />
                    )}
                  </div>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Apelido (opcional)</Label>
                    <SugestaoInput
                      testid="processo-apelido-input"
                      fonte="apelidos"
                      placeholder="Ex: Ação Trabalhista - João"
                      value={form.apelido}
                      onValueChange={(v) => setForm((f) => ({ ...f, apelido: v }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="processo-client-select" className="text-xs">Cliente vinculado</Label>
                    <select
                      id="processo-client-select"
                      data-testid="processo-client-select"
                      value={form.client_id}
                      onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                      className="w-full h-10 px-3 rounded-md border border-stone-300 bg-white text-sm text-stone-900 focus:outline-none focus:ring-2 focus:ring-gold-500/30 focus:border-gold-500"
                    >
                      <option value="none">— Sem cliente —</option>
                      {clients.map((c) => (
                        <option key={c.client_id} value={c.client_id}>{toTitleCasePt(c.name)}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Comarca</Label>
                    <SugestaoInput
                      testid="processo-comarca-input"
                      fonte="comarcas"
                      placeholder="Ex: São Paulo"
                      value={form.comarca}
                      onValueChange={(v) => setForm((f) => ({ ...f, comarca: v }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Vara</Label>
                    <SugestaoInput
                      testid="processo-vara-input"
                      fonte="varas"
                      placeholder="Ex: 2ª Vara do Trabalho"
                      value={form.vara}
                      onValueChange={(v) => setForm((f) => ({ ...f, vara: v }))}
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-xs mb-1.5 block">Tipo de ação</Label>
                  <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                    {CASE_AREAS.map(({ value, label }) => (
                      <button
                        key={value}
                        type="button"
                        data-testid={`processo-new-tipo-${value}`}
                        onClick={() => setForm({ ...form, case_area: value })}
                        className={`px-2 py-2 rounded-md border text-xs font-medium transition-colors ${
                          form.case_area === value
                            ? "bg-stone-900 text-white border-stone-900"
                            : "bg-white text-stone-700 border-stone-200 hover:border-stone-300"
                        }`}
                      >
                        {label}
                      </button>
                    ))}
                  </div>
                  {form.case_area === "outro" && (
                    <Input
                      data-testid="processo-new-tipo-custom"
                      value={form.case_area_custom}
                      onChange={(e) => setForm({ ...form, case_area_custom: e.target.value })}
                      placeholder="Especifique a área (ex: Tributário, Consumidor, Família…)"
                      className="mt-2"
                    />
                  )}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">{(PARTY_LABELS[form.case_area] || PARTY_LABELS.outro)[0]}</Label>
                    <SugestaoInput
                      testid="processo-party-author-input"
                      fonte={["partes", "clientes"]}
                      value={form.party_author}
                      onValueChange={(v) => setForm((f) => ({ ...f, party_author: v }))}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">{(PARTY_LABELS[form.case_area] || PARTY_LABELS.outro)[1]}</Label>
                    <SugestaoInput
                      testid="processo-party-defendant-input"
                      fonte={["partes", "clientes"]}
                      value={form.party_defendant}
                      onValueChange={(v) => setForm((f) => ({ ...f, party_defendant: v }))}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-xs">Valor da causa</Label>
                    <Input
                      data-testid="processo-valor-causa-input"
                      placeholder="R$ 0,00"
                      inputMode="numeric"
                      value={form.valor_causa}
                      onChange={(e) => setForm({ ...form, valor_causa: maskCurrencyInput(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Data de ajuizamento</Label>
                    <Input
                      data-testid="processo-data-ajuizamento-input"
                      type="text"
                      inputMode="numeric"
                      placeholder="dd/mm/aaaa"
                      maxLength={10}
                      value={form.data_ajuizamento_text}
                      onChange={(e) => setForm({ ...form, data_ajuizamento_text: maskDateInput(e.target.value) })}
                    />
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
                <Button
                  data-testid="submit-processo-button"
                  onClick={submit}
                  disabled={!form.numero}
                  className="bg-emerald-brand text-white"
                >
                  Salvar
                </Button>
              </DialogFooter>
            </DialogContent>
    </Dialog>
  );
}
